/**
 * Un dialogo piccolo con pochi campi (il motivo di un rifiuto, numero e data di una fattura, la data di un bonifico):
 * uno solo per tutte le domande brevi del programma partner, invece di un dialogo per ognuna.
 */

import React, { useEffect, useState } from 'react';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';

export interface CampoDialogo {
  nome: string;
  etichetta: string;
  tipo: 'testo' | 'data' | 'testo_lungo';
  obbligatorio?: boolean;
  aiuto?: string;
  iniziale?: string;
}

interface Props {
  aperto: boolean;
  titolo: string;
  campi: CampoDialogo[];
  etichettaConferma: string;
  /** Torna `true` se è andata: il dialogo si chiude. */
  onConferma: (valori: Record<string, string>) => Promise<boolean>;
  onChiudi: () => void;
}

export const DialogoCampi: React.FC<Props> = ({ aperto, titolo, campi, etichettaConferma, onConferma, onChiudi }) => {
  const { t } = useTranslation('partner');
  const [valori, setValori] = useState<Record<string, string>>({});
  const [lavora, setLavora] = useState(false);

  // Si riempie solo all'apertura: chi chiama passa i campi come letterale, nuovo a ogni disegno, e ripartire da capo
  // a ogni disegno cancellerebbe ciò che si sta scrivendo.
  useEffect(() => {
    if (aperto) setValori(Object.fromEntries(campi.map((c) => [c.nome, c.iniziale ?? ''])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aperto]);

  const completo = campi.every((c) => !c.obbligatorio || (valori[c.nome] ?? '').trim().length > 0);

  const conferma = async () => {
    setLavora(true);
    try {
      if (await onConferma(valori)) onChiudi();
    } finally {
      setLavora(false);
    }
  };

  return (
    <Dialog open={aperto} onClose={lavora ? undefined : onChiudi} fullWidth maxWidth="xs">
      <DialogTitle>{titolo}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {campi.map((c) => (
            <TextField
              key={c.nome}
              label={c.etichetta}
              type={c.tipo === 'data' ? 'date' : 'text'}
              multiline={c.tipo === 'testo_lungo'}
              minRows={c.tipo === 'testo_lungo' ? 3 : undefined}
              value={valori[c.nome] ?? ''}
              onChange={(e) => setValori((v) => ({ ...v, [c.nome]: e.target.value }))}
              required={c.obbligatorio}
              helperText={c.aiuto}
              size="small"
              fullWidth
              InputLabelProps={c.tipo === 'data' ? { shrink: true } : undefined}
            />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onChiudi} disabled={lavora} sx={{ textTransform: 'none' }}>{t('azioni.annulla')}</Button>
        <Button
          variant="contained"
          onClick={conferma}
          disabled={!completo || lavora}
          startIcon={lavora ? <CircularProgress size={14} /> : undefined}
          sx={{ textTransform: 'none' }}
        >
          {etichettaConferma}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
